import { Checkbox, IconTooltip } from 'sonata-react-components';

import './checkboxGroup.scss';

export type CheckboxOption = {
  id: string;
  label: string;
  show?: boolean;
  tooltipText?: string;
};

export type CheckboxDivider = {
  id: string;
  dividerText: string;
};

export type CheckboxItem = CheckboxOption | CheckboxDivider;

type Props = {
  id: string;
  onChange: (id: string, selected: string[]) => void;
  options: CheckboxItem[];
  selected: string[];
  title: string;
};

export default function CheckboxGroup({ id, onChange, options, selected, title }: Props) {
  const handleChange = (optionId: string, checked: boolean) => {
    const updated = checked ? [...selected, optionId] : selected.filter(s => s !== optionId);
    onChange(id, updated);
  };

  const checkIfOptionIsVisible = (option: CheckboxItem): boolean => {
    if ('show' in option) {
      return Boolean(option.show);
    }
    return true;
  };

  const filteredOptions = options
    .filter((item): item is CheckboxOption => !('dividerText' in item))
    .filter((item: CheckboxOption) => checkIfOptionIsVisible(item));

  if (filteredOptions.length === 0) {
    return null;
  }

  return (
    <div className="checkboxGroup">
      <div className="checkboxGroup__header">
        <span className="checkboxGroup__headerTitle">{title}</span>
      </div>
      <div className="checkboxGroup__options">
        {options.map(option => {
          if ('dividerText' in option) {
            return (
              <div className="checkboxGroup__divider" key={option.id}>
                {(option as CheckboxDivider).dividerText}
              </div>
            );
          }
          if (!checkIfOptionIsVisible(option)) {
            return null;
          }
          return (
            <div className="checkboxGroup__item" key={option.id}>
              <Checkbox
                active={selected.includes(option.id)}
                id={`${id}-${option.id}`}
                key={option.id}
                labelText={option.label}
                onClickAction={(_optId, checked) => handleChange(option.id, checked)}
                type="checkbox"
              />
              {option.tooltipText && (
                <IconTooltip
                  effect="solid"
                  event="mouseenter"
                  icon="fa-regular fa-circle-info"
                  iconColor="var(--color-sonata-interaction-500) !important"
                  place="left"
                >
                  {option.tooltipText}
                </IconTooltip>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
